import React, { useState, useEffect, useRef } from 'react'
import { FormControl, FormHelperText, InputLabel } from '@material-ui/core'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.bubble.css'

import { StringUtils } from 'utils'

import { useStyles } from './styles'

const TOOLBAR_OPTIONS = ['bold']

const defaultHelperTextStyle = {
  fontFamily: 'Montserrat',
  fontSize: 10,
  marginTop: 4,
}

const defaultInputLabelStyle = {
  fontFamily: 'Montserrat',
  fontWeight: 400,
  fontSize: 14,
  paddingLeft: 10,
  paddingBottom: 8,
  lineHeight: '12px',
}

interface EditorInputProps {
  value?: string
  label?: string
  required?: boolean
  readOnly?: boolean
  maxLength?: number
  error?: boolean
  errorText?: string
  helperText?: string
  onChange?: (changes: string) => void
  style?: React.CSSProperties
}

const htmlPlainLength = (html: string) => StringUtils.extractTextFromHTML(html || '').length

const isRedundantMaxHint = (msg: string, maxHint: string, maxLength: number) =>
  Boolean(
    maxHint &&
      msg &&
      (msg.trim() === maxHint.trim() ||
        (msg.includes('Máximo') && msg.includes(String(maxLength)))),
  )

const EditorInput: React.FC<EditorInputProps> = ({
  value: initialValue = '',
  maxLength = -1,
  error = false,
  errorText = '',
  helperText = '',
  label,
  required,
  readOnly,
  onChange,
  style,
}) => {
  const classes = useStyles()

  const [value, setValue] = useState<string>(initialValue)
  const [isFocused, setIsFocused] = useState(false)
  const [editorLength, setEditorLength] = useState(0)
  const [shouldShrink, setShouldShrink] = useState(false)
  const lastValidRef = useRef<string>(initialValue)

  useEffect(() => {
    setValue(initialValue)
    lastValidRef.current = initialValue
    if (initialValue && initialValue.trim().length > 0) {
      const len = htmlPlainLength(initialValue)
      setEditorLength(len)
      setShouldShrink(true)
    } else {
      setEditorLength(0)
    }
  }, [initialValue])

  const handleEditorContentChange = (content: string) => {
    const currentTextLength = StringUtils.extractTextFromHTML(content || '').length

    if (maxLength > 0 && currentTextLength > maxLength) {
      const revertTo = lastValidRef.current
      setValue(revertTo)
      if (onChange) onChange(revertTo)
      setEditorLength(htmlPlainLength(revertTo))
      return
    }

    lastValidRef.current = content
    setValue(content)
    if (onChange) onChange(content)

    setEditorLength(currentTextLength)
    setShouldShrink(Boolean(isFocused || currentTextLength >= 1))
  }

  const handleFocus = () => {
    setIsFocused(true)
    setShouldShrink(Boolean(true || editorLength >= 1))
  }

  const handleBlur = () => {
    setIsFocused(false)
    setShouldShrink(Boolean(false || editorLength >= 1))
  }

  const showValidationError = error && Boolean(errorText)
  const maxHint = maxLength > 0 ? `Máximo ${maxLength} caracteres` : ''
  const controlError = showValidationError
  const errorHelperRedundant = isRedundantMaxHint(errorText, maxHint, maxLength)
  const nonErrorHelperRedundant = isRedundantMaxHint(helperText, maxHint, maxLength)

  let errorFormHelperText: string | null = null
  if (showValidationError) {
    if (errorHelperRedundant || !maxHint) {
      errorFormHelperText = errorText
    } else {
      errorFormHelperText = `${errorText} · ${maxHint}`
    }
  }

  let okFormHelperText: string | null = null
  if (!showValidationError && maxHint) {
    okFormHelperText =
      helperText && !nonErrorHelperRedundant ? `${helperText} · ${maxHint}` : maxHint
  }

  return (
    <FormControl fullWidth>
      {label && (
        <InputLabel
          id="editor-input-label"
          htmlFor="editor-input"
          required={required}
          shrink={shouldShrink}
          error={controlError}
          style={defaultInputLabelStyle}
        >
          {label}
        </InputLabel>
      )}
      <ReactQuill
        modules={{ toolbar: TOOLBAR_OPTIONS }}
        theme="bubble"
        id="editor-input"
        value={value}
        onChange={handleEditorContentChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        readOnly={readOnly}
        className={classes.quillEditor + (controlError ? ` ${classes.error}` : '')}
        style={style}
      />
      {errorFormHelperText != null && (
        <FormHelperText style={defaultHelperTextStyle} error>
          {errorFormHelperText}
        </FormHelperText>
      )}
      {okFormHelperText != null && (
        <FormHelperText style={defaultHelperTextStyle}>{okFormHelperText}</FormHelperText>
      )}
      {!showValidationError && !maxHint && helperText && (
        <FormHelperText style={defaultHelperTextStyle}>{helperText}</FormHelperText>
      )}
    </FormControl>
  )
}

EditorInput.defaultProps = {
  value: '',
  label: '',
  required: false,
  readOnly: false,
  maxLength: -1,
  error: false,
  errorText: '',
  helperText: '',
  onChange: undefined,
  style: undefined,
}

export { EditorInput }
